// The mileage log behind the CSV and the printable report, ported from the app's MileageReport.

import type { Category, CategoryCatalog } from "./categories.ts";
import type { Trip, Vehicle } from "./model.ts";
import type { Interval } from "./periods.ts";
import { type DistanceUnit, type MileageRates, type RateStart, type ResolvedRate, compareStarts, metersPerUnit, rateValue } from "./rates.ts";

export interface ReportRow {
  tripId: string;
  start: Date;
  end: Date | null;
  from: string | null;
  to: string | null;
  purpose: string | null;
  notes: string | null;
  isRoundTrip: boolean;
  category: Category;
  vehicleName: string | null;
  /** In the report's unit. */
  distance: number;
  rate: ResolvedRate | undefined;
  value: number | undefined;
}

export interface ReportCategoryTotal {
  category: Category;
  trips: number;
  distance: number;
  value: number | undefined;
}

export interface RateUsed {
  category: Category;
  year: number;
  start: RateStart;
  amountTenThousandths: number;
  unit: DistanceUnit;
  currencyCode: string;
}

function trimmed(text: string | null): string | null {
  const value = text?.trim();
  return value ? value : null;
}

export class MileageReport {
  readonly rows: ReportRow[];
  readonly categories: Category[];
  readonly periodTitle: string;
  readonly interval: Interval;
  readonly vehicleName: string | null;
  readonly unit: DistanceUnit;
  readonly generatedAt: Date;

  constructor(options: {
    trips: readonly Trip[];
    vehicles: readonly Vehicle[];
    catalog: CategoryCatalog;
    rates: MileageRates;
    interval: Interval;
    periodTitle: string;
    vehicle: Vehicle | null;
    categories: readonly Category[];
    unit: DistanceUnit;
    generatedAt?: Date;
  }) {
    const { trips, vehicles, catalog, rates, interval, vehicle, unit } = options;
    this.periodTitle = options.periodTitle;
    this.interval = interval;
    this.vehicleName = vehicle?.name ?? null;
    this.unit = unit;
    this.generatedAt = options.generatedAt ?? new Date();
    this.categories = [...options.categories].sort((a, b) => a.listRank - b.listRank);
    const wanted = new Set(this.categories.map((category) => category.rawValue));
    const vehicleNames = new Map(vehicles.map((candidate) => [candidate.id.toUpperCase(), candidate.name]));

    this.rows = trips
      .filter((trip) => {
        const category = catalog.resolve(trip.categoryRaw);
        if (!category || !wanted.has(category.rawValue)) return false;
        if (trip.start < interval.start || trip.start >= interval.end) return false;
        if (vehicle) return trip.vehicleId?.toUpperCase() === vehicle.id.toUpperCase();
        return true;
      })
      .sort((a, b) => a.start.getTime() - b.start.getTime())
      .map((trip): ReportRow => {
        const category = catalog.resolve(trip.categoryRaw)!;
        const rate = rates.rate(category.rawValue, trip.start);
        return {
          tripId: trip.id,
          start: trip.start,
          end: trip.end,
          from: trip.startName,
          to: trip.endName,
          purpose: trimmed(trip.purpose),
          notes: trimmed(trip.notes),
          isRoundTrip: trip.isRoundTrip,
          category,
          vehicleName: trip.vehicleId ? vehicleNames.get(trip.vehicleId.toUpperCase()) ?? null : null,
          distance: trip.meters / metersPerUnit[unit],
          rate,
          value: rate ? rateValue(rate, trip.meters) : undefined,
        };
      });
  }

  get totalDistance(): number {
    return this.rows.reduce((sum, row) => sum + row.distance, 0);
  }

  /** The one currency the values are in, or undefined when rates use several (totals are left out). */
  get currencyCode(): string | undefined {
    const codes = new Set(this.rows.map((row) => row.rate?.currencyCode).filter((code): code is string => !!code));
    return codes.size === 1 ? [...codes][0] : undefined;
  }

  get totalValue(): number | undefined {
    if (!this.currencyCode) return undefined;
    return this.rows.reduce((sum, row) => sum + (row.value ?? 0), 0);
  }

  /** Per category, in menu order, only categories with trips. */
  get categoryTotals(): ReportCategoryTotal[] {
    const currency = this.currencyCode;
    return this.categories.flatMap((category) => {
      const matching = this.rows.filter((row) => row.category.rawValue === category.rawValue);
      if (matching.length === 0) return [];
      const values = matching.map((row) => row.value).filter((value): value is number => value !== undefined);
      return [{
        category,
        trips: matching.length,
        distance: matching.reduce((sum, row) => sum + row.distance, 0),
        value: values.length === 0 || !currency ? undefined : values.reduce((sum, value) => sum + value, 0),
      }];
    });
  }

  /**
   * Every rate a row was valued at, by category then date. A later period at the same rate adds
   * nothing, so it's folded into the earlier one.
   */
  get ratesUsed(): RateUsed[] {
    const unique = new Map<string, RateUsed>();
    for (const row of this.rows) {
      if (!row.rate) continue;
      const rate = row.rate;
      const key = [row.category.rawValue, rate.year, rate.start.month, rate.start.day, rate.amountTenThousandths, rate.unit, rate.currencyCode].join("|");
      unique.set(key, { category: row.category, year: rate.year, start: rate.start, amountTenThousandths: rate.amountTenThousandths, unit: rate.unit, currencyCode: rate.currencyCode });
    }
    const order = this.categories.map((category) => category.rawValue);
    const sorted = [...unique.values()].sort((a, b) =>
      order.indexOf(a.category.rawValue) - order.indexOf(b.category.rawValue)
      || a.year - b.year
      || compareStarts(a.start, b.start));
    const result: RateUsed[] = [];
    for (const rate of sorted) {
      const previous = result.at(-1);
      if (previous && previous.category.rawValue === rate.category.rawValue && previous.year === rate.year
        && previous.amountTenThousandths === rate.amountTenThousandths && previous.unit === rate.unit
        && previous.currencyCode === rate.currencyCode) {
        continue;
      }
      result.push(rate);
    }
    return result;
  }

  /** A file name without extension, e.g. "September 2026 Mileage Log". */
  get baseFileName(): string {
    return `${this.periodTitle} Mileage Log`.replace(/[/\\:?%*|"<>\r\n]/g, " ").replace(/\s+/g, " ").trim();
  }
}
